import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { PurchaseDocDetail, PurchaseVatLine } from "types/InvoiceInbox";
import { setPurchaseVatClassifier } from "services/invoice-inbox.service";
import { formatAmount, isForeignCurrency, toAmount } from "utils/invoiceInboxStatus";
import { formatPrettyDate } from "utils/helperFunctions";
import { MODEL_PVM_REASONS, PVM_CODE_OPTIONS, isUncertainPvmLine, pvmCodeLabel, pvmReasonLabel } from "utils/pvmClassifier";
import { FieldSuggestion, SuggestionAccess, reviewMarkClass } from "./invoice-doc-suggestion";
import { Card, CardRow } from "./invoice-doc-ui";

type Props = {
  companyId: string;
  detail: PurchaseDocDetail;
  reloadAfterChange: () => Promise<void>;
  suggestions: SuggestionAccess;
};

/** Net / VAT / total as read, the EUR equivalent of a foreign-currency
 * document, and the VAT lines with the classifier the accounting export needs. */
export default function InvoiceDocAmountsCard({ companyId, detail, reloadAfterChange, suggestions }: Props) {
  const { t } = useTranslation();
  const { doc, vatLines } = detail;
  const [savingPvmLine, setSavingPvmLine] = useState<string | null>(null);

  // The VAT classifier can be changed until the document has gone to the
  // accounting system — the server refuses it after that (ALREADY_EXPORTED).
  const canEditPvm = !doc.deletedAt && doc.status !== "exported";

  const changePvmClassifier = async (lineId: string, code: string) => {
    setSavingPvmLine(lineId);
    try {
      await setPurchaseVatClassifier(companyId, doc.id, lineId, code === "" ? null : code);
      toast.success(t("invoice_inbox_pvm_saved", { defaultValue: "VAT code saved" }));
      // Reload rather than splice the answer in: the reason line and anything
      // else the classifier touched come back the way the server now sees them.
      await reloadAfterChange();
    } catch (e) {
      toast.error(t("invoice_inbox_pvm_save_failed", { defaultValue: "Could not save the VAT code" }));
    } finally {
      setSavingPvmLine(null);
    }
  };

  const amountRow = (column: "netAmount" | "vatAmount" | "grossAmount", label: string, strong = false) => (
    <div key={column}>
      <CardRow
        label={strong ? <span className="font-semibold text-gray-900">{label}</span> : label}
        className={reviewMarkClass(doc, column)}
      >
        <span className={strong ? "font-semibold" : undefined}>{formatAmount(doc[column], doc.currency)}</span>
      </CardRow>
      <FieldSuggestion doc={doc} column={column} value={doc[column]} access={suggestions} />
    </div>
  );

  const needsReview = doc.needsReviewFields || [];
  return (
    <Card title={t("amounts", { defaultValue: "Amounts" })}>
      {amountRow("netAmount", t("net", { defaultValue: "Net" }))}
      {amountRow("vatAmount", t("vat", { defaultValue: "VAT" }))}
      <div className="mt-1 border-t border-gray-100 pt-1">{amountRow("grossAmount", t("total", { defaultValue: "Total" }), true)}</div>

      {isForeignCurrency(doc) && <EurEquivalent doc={doc} />}

      {/* Shown even for a single line: every line carries the VAT classifier
          the accounting export needs, and the accountant has to be able to
          see and correct it. */}
      {vatLines.length > 0 && (
        <div className="mt-2 space-y-2 border-t border-gray-100 pt-2">
          {vatLines.map((line) => (
            <VatLineRow
              key={line.id}
              line={line}
              currency={doc.currency}
              canEdit={canEditPvm}
              saving={savingPvmLine !== null}
              onChange={(code) => changePvmClassifier(line.id, code)}
            />
          ))}
        </div>
      )}

      {needsReview.length > 0 && (
        <p className="mt-2 text-xs text-amber-700">
          {t("invoice_inbox_needs_check", {
            defaultValue: "Fields marked in amber could not be verified automatically — please check them.",
          })}
        </p>
      )}
    </Card>
  );
}

function EurEquivalent({ doc }: { doc: PurchaseDocDetail["doc"] }) {
  const { t } = useTranslation();
  const rate = toAmount(doc.fxRate ?? null);
  if (rate === null) {
    return (
      <p className="mt-2 rounded border border-gray-200 bg-gray-50 p-2 text-xs text-amber-700">
        {doc.issueDate
          ? t("invoice_inbox_fx_pending", {
              defaultValue:
                "The Bank of Lithuania has not published the rate for the invoice date yet — the EUR amounts will be filled in automatically.",
            })
          : t("invoice_inbox_fx_no_date", { defaultValue: "Enter the invoice date to convert the amounts to EUR." })}
      </p>
    );
  }
  return (
    <div className="mt-2 rounded border border-gray-200 bg-gray-50 px-2 py-1.5">
      <CardRow label={t("net", { defaultValue: "Net" })}>{formatAmount(doc.netAmountEur ?? null, "EUR")}</CardRow>
      <CardRow label={t("vat", { defaultValue: "VAT" })}>{formatAmount(doc.vatAmountEur ?? null, "EUR")}</CardRow>
      <CardRow label={t("total", { defaultValue: "Total" })}>
        <span className="font-semibold">{formatAmount(doc.grossAmountEur ?? null, "EUR")}</span>
      </CardRow>
      <div className="mt-1 text-xs text-gray-500">
        {t("invoice_inbox_fx_rate_line", {
          defaultValue: "Rate 1 EUR = {{rate}} {{currency}} on {{date}}",
          rate,
          currency: doc.currency,
          date: doc.fxRateDate ? formatPrettyDate(doc.fxRateDate) : "—",
        })}{" "}
        ·{" "}
        {doc.fxRateSource === "manual"
          ? t("invoice_inbox_fx_source_manual", { defaultValue: "entered manually" })
          : t("invoice_inbox_fx_source_lb", { defaultValue: "Bank of Lithuania" })}
      </div>
    </div>
  );
}

type VatLineRowProps = {
  line: PurchaseVatLine;
  currency: string;
  canEdit: boolean;
  saving: boolean;
  onChange: (code: string) => void;
};

function VatLineRow({ line, currency, canEdit, saving, onChange }: VatLineRowProps) {
  const { t } = useTranslation();
  const hint = (line.modelPvmHint || "").trim();
  const label = pvmReasonLabel(t, line);
  // The reader's "why" is spelled out when the code came from it (or it
  // disagrees); otherwise it is a tooltip.
  const reason =
    label && hint && line.pvmClassifierSource !== "manual" && MODEL_PVM_REASONS.has(line.pvmClassifierReason || "")
      ? `${label} (${hint})`
      : label;
  const pvmLabel = t("invoice_inbox_pvm_code", { defaultValue: "VAT code" });
  // A code the picker does not list (set elsewhere, or newer than this build)
  // must still show as itself, not as "Automatic".
  const unlisted = line.pvmClassifier && !PVM_CODE_OPTIONS.some((o) => o.code === line.pvmClassifier);

  return (
    <div className="text-sm">
      <div className="flex items-center justify-between gap-3 text-gray-600">
        <span>PVM {line.ratePercent}%</span>
        <span className="tabular-nums">
          {formatAmount(line.taxableValue, currency)} · {formatAmount(line.taxAmount, currency)}
        </span>
      </div>
      <div className="mt-1" data-testid={`pvm-cell-${line.id}`}>
        {canEdit ? (
          <select
            aria-label={pvmLabel}
            className="w-full rounded-lg border border-gray-300 bg-white px-2 py-1 text-base md:text-sm disabled:opacity-50"
            value={line.pvmClassifier ?? ""}
            disabled={saving}
            onChange={(event) => onChange(event.target.value)}
          >
            <option value="">{t("invoice_inbox_pvm_auto", { defaultValue: "Automatic" })}</option>
            {PVM_CODE_OPTIONS.map((option) => (
              <option key={option.code} value={option.code}>
                {pvmCodeLabel(t, option.code)}
              </option>
            ))}
            {unlisted && <option value={line.pvmClassifier as string}>{line.pvmClassifier}</option>}
          </select>
        ) : (
          // The register shows the code's meaning in a tooltip; here it is plain text.
          <span>{line.pvmClassifier ? pvmCodeLabel(t, line.pvmClassifier) : "—"}</span>
        )}
        {reason && (
          <div
            className={`mt-0.5 text-xs ${isUncertainPvmLine(line) ? "text-amber-700" : "text-gray-500"}`}
            title={hint || undefined}
          >
            {reason}
          </div>
        )}
      </div>
    </div>
  );
}
